import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { SideNav } from "@/components/SideNav";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AlertOctagon, Plus, CheckCircle2 } from "lucide-react";
import { formatINR } from "@/lib/format";

export default function AdminIssues() {
  const [issues, setIssues] = useState([]);
  const [stores, setStores] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState({ store_id: "", issue_type: "", details: "", action_required: "", timeline: "" });
  const [tab, setTab] = useState("Open");

  const load = async () => {
    try {
      const [i, s] = await Promise.all([api.get("/issues"), api.get("/stores")]);
      setIssues(i.data); setStores(s.data);
    } catch { toast.error("Failed to load"); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.store_id || !form.issue_type || !form.details) return toast.error("Fill required fields");
    try {
      await api.post("/issues", form);
      toast.success("Issue raised");
      setOpenNew(false); setForm({ store_id: "", issue_type: "", details: "", action_required: "", timeline: "" });
      load();
    } catch { toast.error("Failed"); }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/issues/${id}`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch { toast.error("Failed"); }
  };

  const filtered = issues.filter(i => tab === "All" || i.status === tab);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <AppHeader title="Issue Tracker" subtitle="Machine, stock, and operational issues" />
      <div className="flex-1 flex">
        <SideNav />
        <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 py-6 space-y-4">
          <div className="flex items-center justify-between">
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList>
                <TabsTrigger data-testid="tab-open" value="Open">Open</TabsTrigger>
                <TabsTrigger data-testid="tab-in-progress" value="In Progress">In Progress</TabsTrigger>
                <TabsTrigger data-testid="tab-closed" value="Closed">Closed</TabsTrigger>
                <TabsTrigger data-testid="tab-all" value="All">All</TabsTrigger>
              </TabsList>
            </Tabs>
            <Button data-testid="new-issue-btn" onClick={() => setOpenNew(true)} className="bg-brand-red hover:bg-[#B31721]">
              <Plus className="h-4 w-4 mr-2" /> Raise Issue
            </Button>
          </div>

          <div className="grid gap-3">
            {filtered.map(i => {
              const store = stores.find(s => s.id === i.store_id);
              return (
                <Card key={i.id} className="p-4 rounded-xl border-gray-200 bg-white" data-testid={`issue-${i.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <AlertOctagon className={`h-4 w-4 ${i.status === "Open" ? "text-red-600" : i.status === "In Progress" ? "text-amber-600" : "text-green-600"}`} />
                        <span className="font-semibold text-gray-900">{i.issue_type}</span>
                        <Badge variant="outline" className={
                          i.status === "Open" ? "border-red-300 text-red-700 bg-red-50" :
                          i.status === "In Progress" ? "border-amber-300 text-amber-700 bg-amber-50" :
                          "border-green-300 text-green-700 bg-green-50"
                        }>{i.status}</Badge>
                      </div>
                      <div className="text-sm text-gray-700 mt-1">{i.details}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {store?.store_name || i.store_id} · Owner: {i.owner || "—"} · Timeline: {i.timeline || "—"} · Raised: {i.date}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      {i.status !== "In Progress" && <Button data-testid={`in-progress-${i.id}`} size="sm" variant="outline" onClick={() => updateStatus(i.id, "In Progress")}>Mark in progress</Button>}
                      {i.status !== "Closed" && <Button data-testid={`close-${i.id}`} size="sm" onClick={() => updateStatus(i.id, "Closed")} className="bg-green-600 hover:bg-green-700"><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Close</Button>}
                    </div>
                  </div>
                </Card>
              );
            })}
            {filtered.length === 0 && <div className="text-center py-8 text-gray-500">No issues in {tab}</div>}
          </div>
        </main>
      </div>

      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent data-testid="new-issue-dialog" className="rounded-2xl">
          <DialogHeader><DialogTitle>Raise New Issue</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Select value={form.store_id} onValueChange={(v) => setForm({ ...form, store_id: v })}>
              <SelectTrigger data-testid="issue-store-select"><SelectValue placeholder="Select store" /></SelectTrigger>
              <SelectContent>{stores.map(s => <SelectItem key={s.id} value={s.id}>{s.store_name}</SelectItem>)}</SelectContent>
            </Select>
            <Input data-testid="issue-type-input" placeholder="Issue type (e.g. Machine leak)" value={form.issue_type} onChange={(e) => setForm({ ...form, issue_type: e.target.value })} />
            <Textarea data-testid="issue-details-input" placeholder="Details" rows={3} value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} />
            <Input data-testid="issue-action-input" placeholder="Action required" value={form.action_required} onChange={(e) => setForm({ ...form, action_required: e.target.value })} />
            <Input data-testid="issue-timeline-input" placeholder="Timeline (e.g. 48h)" value={form.timeline} onChange={(e) => setForm({ ...form, timeline: e.target.value })} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenNew(false)}>Cancel</Button>
            <Button data-testid="submit-issue-btn" onClick={create} className="bg-brand-red hover:bg-[#B31721]">Raise</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
